<script setup lang="ts">
import { Prop } from 'vue'
import { FormErrors } from 'vee-validate'
// stores
import { useBasketStore } from '@/store/basket/basket'
import { useBasketTimeStore } from '@/store/basket/basket-time'
// components
import AppButton from '@/components/App/AppButton.vue'

// utils
import { checkSavedCard } from '@/utils/checkSavedCard'

// types
import { Cost } from '@/types/api/api-v3/basket'
import {
  BasketPurchaseButtonEmit,
  CheckoutButtonState,
  CheckoutButtonStateInjectType,
} from '@/types/basket'
import { PayMethods } from '@/types/api/api-v3/orders/order'

const checkoutButtonState = inject<CheckoutButtonStateInjectType>(
  'checkoutButtonState',
)

const basketStore = useBasketStore()
const basketTimeStore = useBasketTimeStore()

const emit = defineEmits([...Object.keys(BasketPurchaseButtonEmit)])
const props = defineProps({
  isSectionSticky: {
    type: Boolean,
    required: true,
  },
  basketErrors: {
    type: Object,
    required: true,
  } as Prop<FormErrors<{}>>,
  cost: {
    type: Object,
    required: true,
  } as Prop<Cost>,
  isAuthenticated: {
    type: Boolean,
    required: true,
  },
  isGiftNeed: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
})

const basketSelectedPaymentMethod = computed(
  () => basketStore.basketSelectedPaymentMethod,
)

const selectedAddress = computed(() => basketStore.selectedAddress)

const selectedGift = computed(() => basketStore.data.selectedGifts[0])

const selectedPaymentMethod = computed(
  () => basketStore.data.selectedPaymentMethod,
)

const purchaseButtonText = computed(() => {
  if (!selectedPaymentMethod.value) {
    return 'Оформить'
  }

  const isOnlineMethod = checkSavedCard(selectedPaymentMethod.value)
    ? true
    : ![PayMethods.CARD, PayMethods.CASH, PayMethods.SAVED_CARD].includes(
        selectedPaymentMethod.value?.type,
      )

  return isOnlineMethod ? 'Оплатить' : 'Оформить'
})

const isExpiredSavedCard = computed(() => {
  // Если выбрали просроченную карту
  return (
    checkSavedCard(selectedPaymentMethod.value) &&
    selectedPaymentMethod.value.is_expired
  )
})

const button = computed(() => {
  if (!props?.isAuthenticated) {
    return {
      text: 'К доставке и оплате',
      emitName: BasketPurchaseButtonEmit.goToAuthorize,
    }
  }

  // Если не выбрали адрес или есть ошибка в deliveryCost
  if (!selectedAddress.value || props.cost?.error_message)
    return {
      text: 'К выбору адреса',
      emitName: BasketPurchaseButtonEmit.goToAddress,
    }

  if (basketTimeStore.error) {
    return {
      text: 'Ко времени',
      emitName: BasketPurchaseButtonEmit.goToDeliveryAt,
    }
  }

  if (props?.cost?.error_message)
    return {
      text: 'В каталог',
      emitName: BasketPurchaseButtonEmit.goToCatalog,
    }

  // TODO: АЛАРМ БЛЯ
  if (
    basketSelectedPaymentMethod.value === 'Не выбрано' ||
    isExpiredSavedCard.value
  )
    return {
      text: 'К способу оплаты',
      emitName: BasketPurchaseButtonEmit.goToPayment,
    }

  if (checkoutButtonState?.get.value === CheckoutButtonState.changeExchange)
    return {
      text: checkoutButtonState.get.value,
      emitName: BasketPurchaseButtonEmit.goToPayment,
    }

  if (!basketStore.promocodeIsValid)
    return {
      text: 'Проверьте промокод',
      emitName: BasketPurchaseButtonEmit.goToDiscount,
    }

  if (props.isGiftNeed && !selectedGift.value)
    return {
      text: 'К выбору подарка',
      emitName: BasketPurchaseButtonEmit.goToGifts,
    }

  return {
    text: purchaseButtonText.value,
    emitName: BasketPurchaseButtonEmit.goToPurchase,
  }
})
const onClickHandler = () => emit(button.value.emitName)
</script>

<template>
  <app-button
    class="button"
    :class="{ 'button--sticky': isSectionSticky }"
    :loading="isLoading"
    @click="onClickHandler"
  >
    {{ button.text }}
  </app-button>
</template>

<style lang="scss" scoped>
.button {
  width: 100%;
  height: 50px;
  overflow: hidden;
  font-size: 16px;
  transition: width 0.2s ease-in-out;

  &--sticky {
    width: 60%;
    overflow: hidden;
    transition: width 0.2s ease-in-out;
  }

  @include media('<tablet') {
    width: 100%;

    &--sticky {
      overflow: hidden;
      transition: width 0.2s ease-in-out;
    }
  }
}
</style>
